import React, {
  useEffect,
  useRef,
  useState
} from "react";
import { Link, useLocation } from "react-router-dom";
import useResizeObserver from "../hooks/use-resize-observer";
import { motion, useAnimation } from 'framer-motion';
import {Helmet} from "react-helmet";

import Cover from "../components/cover";
import allArticles from "../content";
import NotFound from "./not-found";
import SocialMedia from "./../components/social-media";

import closeSrc from '@assets/close.svg';

const findArticle = (slug) => {
  return allArticles.find(a => a.meta.slug === slug)
};

const findNextArticle = (slug) => {
	const currentArticleIndex = allArticles.findIndex(a => a.meta.slug === slug)
	if (currentArticleIndex === allArticles.length - 1) {
		return allArticles[0]
	} else {
		return allArticles[currentArticleIndex + 1]
	}
};

const Article = ({ match, showHeader }) => {
  const location = useLocation();
  // check wether slug matches an article
  const url = location.pathname.split("/")[1];
  const slugs = [];
  allArticles.map((a) => slugs.push(a.meta.slug));
  const isArticle = slugs.includes(url);

  if (!isArticle) {
    return <NotFound />;
  }

	const [ homeTransition, setHomeTransition] = useState(false)
  const articlesRef = useRef(null);
  const coverRef = useRef(null);
	const coverWrapperRef = useRef(null);
  const teaserRef = useRef(null);
  const [article, setArticle] = useState(findArticle(match.params.name))
  const [playAnimation, setPlayAnimation] = useState(false);
	const [{ x, y, w, h }, setSavePositions] = useState({
    x: 0,
    y: 0,
    w: 0,
    h: 0,
  });

	const nextArticle = findNextArticle(match.params.name)

	useEffect(() => {
		setArticle(findArticle(match.params.name))
	}, [location])

  // check client width to show / hide buttons
  const wrapperRef = useRef(null);
  const [width, setWidth] = useState(document.documentElement.clientWidth);

  const handleResize = () => {
    setWidth(document.documentElement.clientWidth);
  };

  useResizeObserver({
    callback: handleResize,
    element: wrapperRef,
  });

	const PageTransitions = {
		exit: {
			opacity: 0.999,
			transition: { duration: 0.4, type: 'Tween' }
		},
		homeExit: {
			opacity: 0.999,
			transition: { duration: 0.4, type: 'Tween' }
		},
		in: {
			opacity: 1,
			transition: { duration: 0.4, type: 'Tween' }
		}
	}

	const HeaderTransitions = {
		out: {
			opacity: 0,
			transition: { duration: 0.3, type: 'Tween' }
		},
		in: {
			opacity: 1,
			transition: { duration: 0.4, type: 'Tween' }
		},
		initial: {
			opacity: 0,
		}
	}

	const ContentTransitions = {
		out: {
			opacity: 0,
			transition: { duration: 0.2, type: 'Tween' }
		},
		in: {
			opacity: 1,
			translateY: 0,
			transition: {
				opacity: { duration: 0.4, delay: 0.8 },
				translateY: { duration: 0.5, delay: 0.8 }
			}
		},
		initial: {
			opacity: 0,
			translateY: 30
		}
	}

	const CoverTransitions = {
		exit: {
			opacity: 0,
			transition: {duration: 0.4, delay: 0.4, type: 'Tween'}
		},
		homeExit: {
			opacity: 0,
			transition: {duration: 0.2, type: 'Tween'}
		}
	}

	const NextArticleTransitions = {
		in: {
			opacity: 1,
			transition: {duration: 0.4, delay: 0.2, type: 'Spring'}
		},
		out: {
			opacity: 0,
			transition: {duration: 0.1, type: 'Spring'}
		}
	}

	const controls = useAnimation()

	useEffect(() => {
		if (!article) return
		controls.start(ref => ({
			width: [location.state?.card.savePosition.w || window.innerWidth / 2, ref.current.offsetWidth], //get articleCover width: ;
			height: [location.state?.card.savePosition.h || window.innerHeight / 2, ref.current.offsetHeight], //get articleCover height
			x: [location.state?.card.savePosition.x || (window.innerWidth / 2) - (ref.current.offsetWidth / 2), ref.current.offsetLeft], //get articleCover position
			y: [location.state?.card.savePosition.y || (window.innerHeight / 2), ref.current.offsetTop], //get articleCover position
			borderRadius: [20, 0],
			transitionEnd: {
				position: 'absolute',
        top: 0,
        left: 0,
        top: -ref.current.offsetTop,
        left: -ref.current.offsetLeft,
				transform: 'none',
        right: "0px",
        width: "100%",
        height: "100%",
			},
			transition: {duration: 0.4, type: 'Tween'}
		}))
		return controls.stop
	}, [article])

	const savePositions = () => {
    const target = teaserRef.current.querySelector(".articleImage");
    const boundingRect = target.getBoundingClientRect();
    setSavePositions({
      x: boundingRect.left,
      y: boundingRect.top,
      w: target.offsetWidth,
      h: target.offsetHeight,
    });
  };

	function onCompleteCover() {
		coverWrapperRef.current.style.position = 'absolute'
		setPlayAnimation(true);
	}

	function onCompleteContent() {
		window.scrollTo(0, 0);
	}

	function onStart() {
	}
	
	function onStartContent() {
		// window.scrollTo(0, 0);
	}

  return (
		<motion.div ref={articlesRef} initial="out" animate="in" exit={homeTransition ? 'homeExit' : 'exit'} variants={PageTransitions} className="articles">
			<Helmet>
				<title>#{article.meta.name} | BWS Jahresbericht 2020</title>
      </Helmet>
			<motion.div initial="out" animate="in" exit="out" variants={HeaderTransitions} className="articlesHeader">
        {article && <div className="articleTag">#{article.meta.name}</div> }
        <Link className="articleClose" onClick={() => setHomeTransition(true)} to={`/`}>
          <img src={closeSrc} />
        </Link>
      </motion.div>
			{ article &&
        <div className="article articleWrapper">
					<motion.h2 initial="initial" animate="in" exit="out" onAnimationComplete={onCompleteContent}  variants={ContentTransitions} className="articleHeadline">
						<span>{article.meta.title}</span>
					</motion.h2>
          <div ref={coverRef} className="articleCover">
            <motion.div
							ref={coverWrapperRef}
							animate={controls}
							variants={CoverTransitions}
							custom={coverRef}
							exit={homeTransition ? 'homeExit' : 'exit'}
							onAnimationStart={onStart}
							onAnimationComplete={onCompleteCover}
							className="articleCoverWrapper"
						>
              {article.meta.animation && (
                <Cover
                  cover={article.meta.cover}
                  id={article.meta.id}
                  animation={article.meta.animation}
                  card={false}
                  play={playAnimation}
									nextAnimation={false}
                />
              )}
              {!article.meta.animation && (
                <div
                  className="articleImage"
                  style={{
                    backgroundImage: `url("assets/cover/${article.meta.slug}.png")`,
                  }}
                ></div>
              )}
            </motion.div>
          </div>
          <motion.div initial="initial" animate="in" exit="out" variants={ContentTransitions} onAnimationStart={onStartContent} className="articleContent">
            { React.createElement(article.component, {}) }
            <div className="articleNextTopline">Als nächstes:</div>
            {nextArticle && (
							<motion.div initial="initial" animate="in" exit="out" variants={ContentTransitions}>
								<Link
									ref={teaserRef}
									onClick={() => savePositions()}
									onMouseDown={() => savePositions() }
									to={{
										pathname: `/${nextArticle.meta.slug}`,
										state: { card: { savePosition: { x: x, y: y, w: w, h: h } } },
									}}
									className={`articleTeaser articleTeaser--${nextArticle.meta.category}`}
								>
									<motion.div initial="out" animate="in" exit="out" variants={NextArticleTransitions}  className="articleTeaserCover">
										{nextArticle.meta.animation && (
											<Cover
												cover={nextArticle.meta.cover}
												id={nextArticle.meta.id}
												animation={nextArticle.meta.animation}
												card={true}
												play={false}
												nextAnimation={true}
											/>
										)}
										{!nextArticle.meta.animation && (
											<div
												className="articleImage"
												style={{
													backgroundImage: `url("assets/cover/${nextArticle.meta.slug}.png")`,
												}}
											></div>
										)}
									</motion.div>
									<div className="articleTeaserInfo">
										{!nextArticle.meta.excerpt && (
											<div>
												<div className="articleTeaserTitle">
													{nextArticle.meta.title}
												</div>
												<div className="articleTeaserHint">
													Weiter zum nächsten Artikel!
												</div>
											</div>
										)}
										<div className="articleTag articleTag--teaser">
											#{nextArticle.meta.name}
										</div>
										{nextArticle.meta.excerpt && (
											<div>
												<div className="articleTeaserTitle articleTeaserTitle--small">
													{nextArticle.meta.title}
												</div>
												<div className="articleTeaserExcerpt">
													<p>{nextArticle.meta.excerpt.before}
													<span className='italic'>{nextArticle.meta.excerpt.italic}</span>
													{nextArticle.meta.excerpt.after}</p>
												</div>
											</div>
										)}
									</div>
								</Link>
							</motion.div>
            )}
            <div className="articleFooter">
              <div className="articleFooterWrapper">
                <Link
                  to={{ pathname: `https://www.bwstiftung.de/de/` }}
                  target="_blank"
                >
                  © Baden-Württemberg Stiftung gGmbH
                </Link>
                <SocialMedia />
                <div className="articleFooterLegal">
                  <Link onClick={() => setHomeTransition(true)} to={`/impressum`}>
                    Impressum
                  </Link>{" "}
                  |{" "}
                  <Link onClick={() => setHomeTransition(true)} to={`/datenschutz`}>
                    Datenschutz
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
			}
    </motion.div>
  );
};

export default Article;
